import { motion } from "framer-motion";
import { Shield, Swords, Clock, ArrowRight } from "lucide-react";
import { GameLeanBar } from "./MarketLean";
import { formatGameTime } from "../lib/api";
import { TeamLogo } from "./TeamLogo";

const RankPill = ({ label, rank, total }) => {
  const pct = (rank - 1) / Math.max(1, total - 1);
  const color = pct < 0.34 ? "#10B981" : pct > 0.66 ? "#EF4444" : "#F59E0B";
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="text-slate-400">{label}</span>
      <span className="font-data font-semibold" style={{ color }}>
        #{rank}
      </span>
    </div>
  );
};

const TeamRow = ({ team, side }) => (
  <div
    className="flex items-center gap-2.5 flex-1 min-w-0 rounded-lg p-2"
    style={{
      background: `linear-gradient(90deg, ${team.color}2e, ${team.color}0a)`,
      boxShadow: `inset 3px 0 0 ${team.color}`,
    }}
  >
    <TeamLogo sport={team.sport} abbr={team.abbr} color={team.color} size="lg" />
    <div className="min-w-0">
      <div className="text-white font-semibold text-sm truncate">{team.name}</div>
      <div className="text-[11px] text-slate-400">{side}</div>
    </div>
  </div>
);

export const MatchupCard = ({ matchup, index, onOpen }) => {
  const { home_team, away_team } = matchup;
  return (
    <motion.button
      data-testid={`matchup-card-${matchup.away_abbr}-${matchup.home_abbr}`.toLowerCase()}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05 }}
      onClick={() => onOpen(matchup)}
      className="group text-left relative overflow-hidden rounded-2xl border border-[#233248] bg-[#141C2B] hover:bg-[#1B273E] hover:border-[#00E5FF]/40 transition-all p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <span className="font-data text-[11px] text-slate-500 flex items-center gap-1">
          <Clock className="h-3 w-3" /> {formatGameTime(matchup.commence_time, matchup.game_time)}
        </span>
        <span className="font-data text-[11px] text-[#00E5FF]">
          {matchup.props_loaded ? `${matchup.prop_count} props` : "load on open"}
        </span>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <TeamRow team={away_team} side="Away" />
        <Swords className="h-4 w-4 text-slate-600 shrink-0" />
        <TeamRow team={home_team} side="Home" />
      </div>

      <div className="grid grid-cols-2 gap-x-5 gap-y-1.5 pt-3 border-t border-[#233248]/70">
        <RankPill label="Off" rank={away_team.offense_rank} total={away_team.total_teams} />
        <RankPill label="Off" rank={home_team.offense_rank} total={home_team.total_teams} />
        <RankPill label="Def" rank={away_team.defense_rank} total={away_team.total_teams} />
        <RankPill label="Def" rank={home_team.defense_rank} total={home_team.total_teams} />
      </div>

      {matchup.total ? (
        <div className="mt-4 pt-3 border-t border-[#233248]/70">
          <GameLeanBar sport={matchup.sport} spread={matchup.spread} away={away_team} home={home_team} />
        </div>
      ) : null}

      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#233248]/70">
        <div className="flex gap-4 font-data text-xs text-slate-300">
          {matchup.total ? (
            <>
              <span>Spread <b className="text-white">{matchup.spread > 0 ? `+${matchup.spread}` : matchup.spread}</b></span>
              <span>O/U <b className="text-white">{matchup.total}</b></span>
            </>
          ) : (
            <span className="text-slate-500">Lines TBD</span>
          )}
        </div>
        <span className="flex items-center gap-1 text-xs font-semibold text-[#00E5FF] group-hover:gap-2 transition-all">
          Analyze <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </motion.button>
  );
};
